import { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkle,
  PaperPlaneRight,
  Lightning,
  ClockCounterClockwise,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Comment brancher un différentiel 30mA type A ?",
  "Différence entre RJ45 cat 6 et cat 6a ?",
  "Code erreur E1 sur ma pompe à chaleur, que faire ?",
  "Schéma va-et-vient avec 2 interrupteurs",
];

const newSessionId = () => `ai-${Date.now()}`;

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(newSessionId);
  const [sessions, setSessions] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const loadSessions = async () => {
    try {
      const r = await api.get("/ai/sessions?limit=50");
      setSessions(r.data.sessions || []);
    } catch (err) {
      // silent — drawer just stays empty
      console.debug("sessions load", err);
    }
  };
  useEffect(() => { loadSessions(); }, []);

  const openSession = async (sid) => {
    setDrawerOpen(false);
    setSessionId(sid);
    try {
      const r = await api.get(`/ai/messages/${sid}`);
      setMessages((r.data || []).map((m) => ({ role: m.role, content: m.content })));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startNewSession = () => {
    setDrawerOpen(false);
    setSessionId(newSessionId());
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    try {
      await api.delete(`/ai/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (sid === sessionId) startNewSession();
      toast.success("Conversation supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: q }),
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.text();
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: `Erreur: ${err.slice(0, 200)}` };
          return c;
        });
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          if (!chunk.startsWith("data: ")) continue;
          const payload = chunk.slice(6);
          if (payload === "[DONE]") continue;
          setMessages((m) => {
            const c = [...m];
            const last = c[c.length - 1];
            c[c.length - 1] = { ...last, content: (last.content || "") + payload };
            return c;
          });
        }
      }
      // Refresh sidebar sessions so the just-answered chat surfaces at the top
      loadSessions();
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: `Erreur: ${e.message}` };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem-5rem)]">
      {/* Header banner */}
      <div className="p-4 pb-2">
        <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/20 to-[#005CFF]/5 border border-[#005CFF]/30 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
            <Sparkle size={22} weight="fill" color="white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Assistant technique</div>
            <div className="text-xs text-white/60">Powered by Claude Sonnet 4.6 · MCP tools BKPRO</div>
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-[#005CFF]/40 flex items-center justify-center relative"
            data-testid="ai-history-open"
            title="Historique des conversations"
          >
            <ClockCounterClockwise size={16} weight="duotone" className="text-white/80" />
            {sessions.length > 0 && (
              <span className="absolute -top-1 -right-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#005CFF] text-white font-mono">
                {sessions.length}
              </span>
            )}
          </button>
          <button
            onClick={startNewSession}
            className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/40 flex items-center justify-center"
            data-testid="ai-new-session"
            title="Nouvelle conversation"
          >
            <Plus size={16} weight="bold" className="text-emerald-300" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 space-y-3" data-testid="ai-messages">
        {messages.length === 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Suggestions</div>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-left rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#005CFF]/60 p-3 active:scale-[0.98] transition-transform" data-testid={`ai-suggest-${s.slice(0, 12)}`}>
                  <div className="flex items-center gap-2">
                    <Lightning size={14} className="text-[#005CFF]" weight="fill" />
                    <div className="text-sm">{s}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#005CFF] text-white" : "bg-white/5 border border-white/10 text-white"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex gap-1"><Dot /><Dot d={150} /><Dot d={300} /></span> : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="p-3 border-t border-white/10 glass-dark"
      >
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Posez votre question…"
            className="h-12 rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/30"
            data-testid="ai-input"
            disabled={streaming}
          />
          <Button type="submit" disabled={streaming || !input.trim()} className="h-12 w-12 rounded-2xl bg-[#005CFF] hover:bg-[#0047CC] p-0" data-testid="ai-send">
            <PaperPlaneRight size={18} weight="bold" />
          </Button>
        </div>
      </form>

      {/* Sessions drawer */}
      {drawerOpen && (
        <SessionsDrawer
          sessions={sessions}
          currentSessionId={sessionId}
          onClose={() => setDrawerOpen(false)}
          onOpen={openSession}
          onDelete={deleteSession}
          onNew={startNewSession}
        />
      )}
    </div>
  );
}

function SessionsDrawer({ sessions, currentSessionId, onClose, onOpen, onDelete, onNew }) {
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="ai-history-drawer">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[#0A0A0A] border-l border-white/10 flex flex-col animate-in slide-in-from-right"
      >
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center">
            <ClockCounterClockwise size={16} className="text-[#7CB2FF]" weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Historique</div>
            <div className="text-[11px] text-white/50">{sessions.length} conversation{sessions.length > 1 ? "s" : ""}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:border-white/20" data-testid="ai-history-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-3">
          <Button onClick={onNew} className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="ai-history-new">
            <Plus size={14} weight="bold" /> Nouvelle conversation
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1.5">
          {sessions.length === 0 ? (
            <div className="p-6 text-center text-sm text-white/40">
              Aucune conversation encore.
            </div>
          ) : (
            sessions.map((s) => {
              const active = s.session_id === currentSessionId;
              const dt = s.last_at ? new Date(s.last_at) : null;
              const dateLabel = dt
                ? dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                : "";
              return (
                <button
                  key={s.session_id}
                  onClick={() => onOpen(s.session_id)}
                  className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${
                    active ? "bg-[#005CFF]/15 border-[#005CFF]/50" : "bg-white/[0.03] border-white/10 hover:border-white/20"
                  }`}
                  data-testid={`ai-history-item-${s.session_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate text-white/90">{s.title}</div>
                      <div className="text-[10.5px] text-white/50 line-clamp-2 mt-0.5">{s.last_snippet}</div>
                      <div className="text-[10px] text-white/40 mt-1 flex items-center gap-1.5">
                        <span>{s.message_count} messages</span>
                        <span>·</span>
                        <span>{dateLabel}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => onDelete(s.session_id, e)}
                      className="text-white/30 hover:text-red-400 p-1"
                      data-testid={`ai-history-del-${s.session_id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Dot({ d = 0 }) {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: `${d}ms` }} />;
}
